import { Dispatch, SetStateAction, useEffect, useState, useRef } from "react";

import type { iVehicleStock } from "./types";
import { useApiService } from "./useApiService";

type UseVehicleStockDataProps = {
  setErrorState: Dispatch<SetStateAction<boolean>>;
  stockDealerId: string | undefined;
  materialCode: string | undefined;
};

const VITE_BUILD_ENV = import.meta.env.VITE_BUILD_ENV;

const useVehicleStockData = ({
  setErrorState,
  stockDealerId,
  materialCode,
}: UseVehicleStockDataProps) => {
  const { apiGetVehicleStock } = useApiService();
  const [vehicleStockData, setVehicleStockData] = useState<iVehicleStock>();
  const latestRequest = useRef(0);

  // *******************
  // URL Parameter Override for Testing
  // *******************
  // Use ?qaStock to simulate stock, ?canOrderTest=<true/false> to simulate can order
  const urlParams = new URLSearchParams(globalThis.window.location.search);
  const qaStockParam = urlParams.get("qaStock");
  const qaCanOrderParam = urlParams.get("qaCanOrder");

  // *******************
  // Vehicle Stock/Pricing Data
  // *******************
  useEffect(() => {
    const getVehicleStockData = async (
      stockDealerId: string,
      matCode: string,
      requestId: number,
    ) => {
      const vehicleStockData = await apiGetVehicleStock(stockDealerId, matCode);

      if (requestId !== latestRequest.current) {
        return;
      }

      const allowQaOverrides = VITE_BUILD_ENV === "dev" || VITE_BUILD_ENV === "staging";

      // Apply URL parameter overrides for testing (even if API returns undefined)
      if (allowQaOverrides) {
        const testData = vehicleStockData || ({} as iVehicleStock);

        if (qaStockParam !== null) {
          testData.numberInStock = qaStockParam === "true" ? 10 : 0;
        }

        if (qaCanOrderParam !== null) {
          testData.canOrder = qaCanOrderParam === "true";
        }

        if (qaStockParam !== null || qaCanOrderParam !== null) {
          setVehicleStockData(testData);
          return;
        }
      }

      if (vehicleStockData) {
        setVehicleStockData(vehicleStockData);
      }
    };

    if (stockDealerId && materialCode) {
      const requestId = Date.now();
      latestRequest.current = requestId;
      getVehicleStockData(stockDealerId, materialCode, requestId);
    }
  }, [
    stockDealerId,
    materialCode,
    apiGetVehicleStock,
    setErrorState,
    qaStockParam,
    qaCanOrderParam,
  ]);

  return { vehicleStockData };
};

export default useVehicleStockData;
